import { axios } from "@/libraries/axios";
import cookie from "@/libraries/cookie";
import {
	deleteAccount,
	deleteAccountSuccess,
	forgotPassword,
	forgotPasswordFailure,
	forgotPasswordSuccess,
	phoneNumber,
	phoneNumberFailure,
	phoneNumberSuccess,
	postRegister,
	postRegisterFailure,
	postRegisterSuccess,
	preForgotPassword,
	preForgotPasswordFailure,
	preForgotPasswordSuccess,
	preSignAvatar,
	preSignAvatarFailure,
	preSignAvatarSuccess,
	redirectUser,
	redirectUserSuccess,
	register,
	registerFailure,
	registerSuccess,
	resendOTP,
	resendOTPFailure,
	resendOTPSuccess,
	subcsrition,
	subcsritionFailure,
	subcsritionSuccess,
	updatePassword,
	updatePasswordFailure,
	updatePasswordSuccess,
	verification,
	verificationFailure,
	verificationSuccess,
	verifyPassword,
	verifyPasswordFailure,
	verifyPasswordSuccess,
} from "@/redux/reducers/auth";
import { authService } from "@/services";
import { AxiosError } from "axios";
import debounce from "lodash/debounce";

async function preForgotPasswordListener({ payload }: any, { dispatch }: any) {
	try {
		const res = await axios.get("/api/v1/auth/password/edit.json", {
			params: payload,
		});
		if (res.headers["access-token"]) {
			authService.setToken(res.headers);
			// await getUserListener({payload}, { dispatch })
			dispatch(preForgotPasswordSuccess({ data: res.data }));
			if (payload.callback) {
				payload.callback();
			}
		} else {
			dispatch(preForgotPasswordFailure({ data: res.data }));
		}
	} catch (error) {
		if (error instanceof AxiosError) {
			dispatch(preForgotPasswordFailure({ data: error.response?.data }));
		}
	}
}

async function forgotPasswordListener({ payload }: any, { dispatch }: any) {
	try {
		const res = await axios.post("/api/v1/auth/password.json", payload);
		dispatch(forgotPasswordSuccess({ data: res.data }));
		payload.callback?.();
	} catch (error) {
		if (error instanceof AxiosError) {
			dispatch(forgotPasswordFailure({ data: error.response?.data }));
		}
	}
}

async function updatePasswordListener({ payload }: any, { dispatch }: any) {
	try {
		const res = await axios.post("/api/v1/auth/password/update", payload);
		dispatch(updatePasswordSuccess({ data: res.data }));
		payload.callback?.();
	} catch (error) {
		if (error instanceof AxiosError) {
			dispatch(updatePasswordFailure({ data: error.response?.data }));
		}
	}
}

async function registeredListener({ payload }: any, { dispatch }: any) {
	try {
		const res = await axios.post("/api/v1/auth.json", {
			user: payload,
		});
		cookie.set(import.meta.env.VITE_COOKIE_TOKEN, res.headers["access-token"]);
		cookie.set(import.meta.env.VITE_COOKIE_CLIENT, res.headers.client);
		cookie.set(import.meta.env.VITE_COOKIE_UID, res.headers.uid);
		dispatch(registerSuccess({ data: res.data }));
		payload.callback?.();
	} catch (error) {
		if (error instanceof AxiosError) {
			dispatch(registerFailure({ data: error.response?.data }));
		}
	}
}

async function postRegisterListener({ payload }: any, { dispatch }: any) {
	try {
		const { id, ...restPayload } = payload;
		const res = await axios.post(`/api/v1/members/${id}.json`, {
			user: restPayload,
		});
		dispatch(postRegisterSuccess({ data: res.data }));
		payload.callback?.();
	} catch (error) {
		if (error instanceof AxiosError) {
			dispatch(postRegisterFailure({ data: error.response?.data }));
		}
	}
}

async function presignAvatarListener({ payload }: any, { dispatch }: any) {
	try {
		const res = await axios.get("/avatar_presign", {
			params: {
				filename: payload.file.name,
			},
		});
		const { data } = res;
		const formData = new FormData();
		for (const key in data.fields) {
			formData.append(key, data.fields[key]);
		}
		formData.append("file", payload.file);
		await postAvatarData(data.url, formData);
		await postRegisterListener(
			{ payload: { id: payload.userId, file_id: data.fields.key } },
			{ dispatch },
		);
		dispatch(preSignAvatarSuccess({ data }));
	} catch (error) {
		if (error instanceof AxiosError) {
			dispatch(preSignAvatarFailure({ data: error.response?.data }));
		}
	}
}

async function postAvatarData(url: any, payload: any) {
	try {
		axios.defaults.headers.common.uid = undefined;
		axios.defaults.headers.common.client = undefined;
		axios.defaults.headers.common["access-token"] = undefined;
		await axios.post(url, payload, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
	} catch (error) {
		return Promise.reject(error);
	}
}

async function phoneNumberListener({ payload }: any, { dispatch }: any) {
	try {
		const res = await axios.post("/api/v1/verifications/phone_number.json", {
			user: payload,
		});
		dispatch(phoneNumberSuccess({ data: res.data }));
		payload.callback?.(res.data);
	} catch (error) {
		if (error instanceof AxiosError) {
			dispatch(phoneNumberFailure({ data: error.response?.data }));
		}
	}
}

async function resendOTPListener({ payload }: any, { dispatch }: any) {
	try {
		const res = await axios.post("/api/v1/verifications/resend.json", {
			user: payload,
		});
		dispatch(resendOTPSuccess({ data: res.data }));
		payload.callback?.(res.data);
	} catch (error) {
		if (error instanceof AxiosError) {
			dispatch(resendOTPFailure({ data: error.response?.data }));
		}
	}
}

async function verificationsListener({ payload }: any, { dispatch }: any) {
	try {
		const res = await axios.post("/api/v1/verifications/verify.json", {
			verify_code: payload.verify_code,
		});
		dispatch(verificationSuccess({ data: res.data }));
		if (payload.callback) {
			payload.callback();
		}
	} catch (error) {
		if (error instanceof AxiosError) {
			dispatch(verificationFailure({ data: error.response?.data }));
		}
	}
}

async function redirectListener({ payload }: any, { dispatch }: any) {
	try {
		const res = await axios.get("/api/v1/redirects.json", {
			params: payload,
		});
		const { data } = res;
		dispatch(redirectUserSuccess({ data }));

		const url = new URL(data.redirect_url);
		const token = url.searchParams.get("token");

		if (token) {
			// Attempt to navigate to the deep link
			const promisify = new Promise((resolve, reject) => {
				// Try to change the window location
				window.location.href = data.redirect_url;

				// Set a timeout to check if the navigation succeeded
				const timeout = setTimeout(() => {
					reject(new Error("Failed to launch URL with the custom scheme."));
				}, 1500); // Adjust the timeout duration as needed

				// Listen for the page unload event
				window.addEventListener("unload", () => {
					clearTimeout(timeout);
					resolve(true);
				});
			});

			promisify.catch((error) => {
				// If there's an error, or the timeout is reached, handle it here
				console.error("Error launching URL:", error);
				window.location.href = "/account"; // Fallback to a different URL
			});
		} else {
			console.error("Token not found in the URL.");
		}
	} catch (error) {
		dispatch(
			registerFailure({ error: "An error occurred. Please try again." }),
		);
	}
}

async function subscriptionListener({ payload }: any, { dispatch }: any) {
	try {
		const response = await axios.post(
			"/api/v1/subscriptions/cancel.json",
			payload,
		);
		const { data } = response;
		dispatch(subcsritionSuccess({ data }));
		payload.callback?.();
	} catch (error) {
		if (error instanceof AxiosError) {
			dispatch(
				subcsritionFailure({
					error: `error ${error.status} : ${error.response?.data}`,
				}),
			);
		}
	}
}

async function deleteMemberAccountListener(
	{ payload }: any,
	{ dispatch }: any,
) {
	try {
		const response = await axios.delete("/api/v1/members/delete_account.json");
		const { data } = response;
		dispatch(deleteAccountSuccess({ data }));
		payload.callback?.();
	} catch (error) {
		if (error instanceof AxiosError) {
			dispatch(
				subcsritionFailure({
					error: `error ${error.status} : ${error.response?.data}`,
				}),
			);
		}
	}
}

async function verifyPasswordListener({ payload }: any, { dispatch }: any) {
	try {
		const response = await axios.post(
			"/api/v1/members/verify_password.json",
			payload,
		);
		const { data } = response;
		dispatch(verifyPasswordSuccess({ data }));
		payload.callback?.();
	} catch (error) {
		if (error instanceof AxiosError) {
			dispatch(
				verifyPasswordFailure({
					error: `error ${error.status} : ${error.response?.data}`,
				}),
			);
		}
	}
}

export default function rootListener(listen: any) {
	listen(preForgotPassword, debounce(preForgotPasswordListener, 100));
	listen(forgotPassword, debounce(forgotPasswordListener, 100));
	listen(updatePassword, debounce(updatePasswordListener, 100));
	listen(register, debounce(registeredListener, 100));
	listen(postRegister, debounce(postRegisterListener, 100));
	listen(preSignAvatar, debounce(presignAvatarListener, 100));
	listen(phoneNumber, debounce(phoneNumberListener, 100));
	listen(verification, debounce(verificationsListener, 100));
	listen(redirectUser, debounce(redirectListener, 100));
	listen(subcsrition, debounce(subscriptionListener, 100));
	listen(deleteAccount, debounce(deleteMemberAccountListener, 100));
	listen(resendOTP, debounce(resendOTPListener, 100));
	listen(verifyPassword, debounce(verifyPasswordListener, 100));
}
